/**
 * Handles DataLayer entries
 */
export class AnalyticsService {
  private userId: null | string;
  private leadId: null | string;

  private analyticsData: Record<string, string> = {
    step_number: '',
    step_name: '',
    transaction_id: '',
    userId: '',
    verification_method: '',
    language_version: '',
    residency: '',
    currency: '',
    us_taxpayer: '',
    politically_exposed: '',
    data_user: '',
    purpose: '',
    source_of_origin: '',
  };

  constructor() {
    this.userId = null;
    this.leadId = null;
  }

  public setData(data: AnalyticsData): void {
    Object.entries(data).forEach(entry => {
      if (entry[1]) {
        this.analyticsData[entry[0]] = entry[1];
      }
    })
  }

  public sendStepEvent(stepName: string) {
    if (window.g4 && stepName) {
      window.dispatchEvent(new CustomEvent('SendEvent', {
        detail: {
          name: 'page_load',
          options: Object.assign({}, this.analyticsData, {
            step_number: this.getStepNumber(stepName),
            step_name: this.getAnalyticsStepName(stepName)
          })
        }
      }))
    } else {
      let interval: number;
      interval = window.setInterval(() => {
        if (window.g4) {
          this.sendStepEvent(stepName)
          clearInterval(interval)
        }
      }, 1000)
    }
  }

  private getAnalyticsStepName(stepName: string): string | null {
    const step = stepMap[stepName]

    if (step) {
      return step.name;
    }

    return null;
  }

  private getStepNumber(stepName: string): string | null {
    const step = stepMap[stepName]

    if (step) {
      return step.number;
    }

    return null;
  }
}

const stepMap: Record<string, AnalyticsStep> = {
  '': {
    number: '0',
    name: 'onb_entry'
  },
  'newAccount': {
    number: '0',
    name: 'onb_entry'
  },
  'residenceConfirm': {
    number: '1',
    name: 'onb_what_residence'
  },
  'chooseLanguage': {
    number: '1.1',
    name: 'onb_choose_language'
  },
  'newRegistration': {
    number: '2',
    name: 'onb_registration_start'
  },
  'newLogin': {
    number: '1',
    name: 'onb_login'
  },
  'restart': {
    number: '1',
    name: 'onb_login'
  },
  'dataUploadChoice': {
    number: '3',
    name: 'onb_registration_method'
  },
  'mobileDesktopChoice': {
    number: '4',
    name: 'onb_select_device'
  },
  'desktopDocumentChoice': {
    number: '4.1',
    name: 'onb_upload_doc'
  },
  'howToTakePicture': {
    number: '4.2',
    name: 'onb_picture'
  },
  'desktopDocumentUpload': {
    number: '4.3',
    name: 'onb_upload_doc'
  },
  'uploadFailed': {
    number: '4.3.1',
    name: 'onb_upload_doc_failed'
  },
  'uploadSucceeded': {
    number: '4.4',
    name: 'onb_upload_doc_success'
  },
  'uploadException': {
    number: '4.5',
    name: 'onb_upload_doc_exception'
  },
  'mobileSms': {
    number: '5',
    name: 'onb_phone_number'
  },
  'mobileDesktopIsCompleted': {
    number: '6',
    name: 'onb_waiting_for_mobile'
  },
  'mobileDocumentChoice_M': { // all entries with `_M` suffix are for mobile route. Suffix is set in NavigationService::goToRoute
    number: '6.1',
    name: 'onb_document_selection'
  },
  'howToTakePicture_M': {
    number: '6.2',
    name: 'onb_picture'
  },
  'mobileReturnToDesktop_M': {
    number: '6.2.1',
    name: 'onb_return_desktop'
  },
  'mobileDocumentUpload_M': {
    number: '6.3',
    name: 'onb_upload_doc'
  },
  'mobileDocumentUploadBack_M': {
    number: '6.4',
    name: 'onb_upload_doc_back'
  },
  'uploadSucceeded_M': {
    number: '6.5',
    name: 'onb_upload_doc_success'
  },
  'uploadException_M': {
    number: '6.6',
    name: 'onb_upload_doc_exception'
  },
  'personalDataConfirm': {
    number: '7',
    name: 'onboarding_personal_data'
  },
  'desktopSms': {
    number: '8',
    name: 'onboarding_confirm_phone'
  },
  'confirmEmail': {
    number: '8.1',
    name: 'onboarding_confirm_mail'
  },
  'addressDetails': {
    number: '9',
    name: 'onboarding_residence_details'
  },
  'taxOfficeDetails': {
    number: '10',
    name: 'onboarding_tax_residence'
  },
  'currencyChoice': {
    number: '11',
    name: 'onboarding_currency'
  },
  'riskAssessment': {
    number: '12',
    name: 'onboarding_finance_profile'
  },
  'declarationAcceptance': {
    number: '13',
    name: 'onboarding_declaration'
  },
  'errorsReport': {
    number: '14',
    name: 'onboarding_account_open'
  },
};

interface AnalyticsStep {
  number: string,
  name: string,
}

export interface AnalyticsData {
  step_number?: string,
  step_name?: string,
  transaction_id?: string,
  userId?: string,
  verification_method?: string,
  language_version?: string,
  residency?: string,
  currency?: string,
  us_taxpayer?: string,
  politically_exposed?: string,
  data_user?: string,
  purpose?: string,
  source_of_origin?: string,
}
